import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { API_ROUTES, APP_ROUTES } from '@/common/constants'
import { useFrankieOneInitKyc } from '@/kyc/frankieOne/useFrankieOneInitKyc'
import { useFetchKYC } from '@/kyc/kycQueries'
import { useKycCompletionState } from '@/kyc/hooks/useKycCompletionState'
import { resolveFrankieOneFlowContext, type FrankieOneFlowContext } from './config/frankieOneFormConfig'

interface UseFrankieOneKycLogicResult {
    token: string | undefined
    isPending: boolean
    isError: boolean
    error: Error | null
    isSuccess: boolean
    isKYCStart: boolean
    startInitKyc: () => void
    onComplete: () => void
    pathname: string
    activeAccountId: string | null | undefined
    target: string | null
    flowContext: FrankieOneFlowContext
}

export function useFrankieOneKycLogic(): UseFrankieOneKycLogicResult {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const { pathname, activeAccountId, isKYCStart, startInitKyc, target, kyc, isPending, isSuccess, isError, error } =
        useFrankieOneInitKyc()
    const { data: kycStatus } = useFetchKYC(activeAccountId ?? null)
    const { markKycCompleted } = useKycCompletionState(kycStatus)

    const flowContext = useMemo(() => resolveFrankieOneFlowContext({ pathname, target }), [pathname, target])

    const onComplete = useCallback(() => {
        // Mark KYC as completed to start grace period
        markKycCompleted()

        if (activeAccountId) {
            queryClient.invalidateQueries({ queryKey: [API_ROUTES.accountsKyc, activeAccountId] })
        }
        navigate(APP_ROUTES.BANK_ACCOUNT)
    }, [queryClient, activeAccountId, navigate, markKycCompleted])

    return {
        token: kyc?.token,
        isPending,
        isError,
        error,
        isSuccess,
        isKYCStart,
        startInitKyc,
        onComplete,
        pathname,
        activeAccountId,
        target,
        flowContext,
    }
}
